import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, CheckCircle, XCircle, Clock, Filter, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';
import { toast } from 'sonner';

const STATUS_STYLES = {
  processed: 'bg-green-100 text-green-700 border-green-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
  skipped: 'bg-slate-100 text-slate-600 border-slate-200',
};

const STATUS_ICONS = {
  processed: <CheckCircle size={12} />,
  failed: <XCircle size={12} />,
  skipped: <Clock size={12} />,
};

const HANDLED_AS_STYLES = {
  subscription: 'bg-indigo-100 text-indigo-700',
  ai_addon: 'bg-violet-100 text-violet-700',
  legacy_payment: 'bg-amber-100 text-amber-700',
};

export default function WebhookLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeSearch, setTypeSearch] = useState('');
  const [typeInput, setTypeInput] = useState('');

  const fetchLogs = useCallback(async (pg = page, status = statusFilter, etype = typeSearch) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, per_page: 50 });
      if (status && status !== 'all') params.append('status', status);
      if (etype) params.append('event_type', etype);
      const res = await api.get(`/admin/webhook-logs/stripe?${params}`);
      setLogs(res.data.logs);
      setTotal(res.data.total);
      setPage(res.data.page);
      setTotalPages(res.data.total_pages);
    } catch (e) {
      toast.error('Failed to load webhook logs');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, typeSearch]);

  useEffect(() => { fetchLogs(1, statusFilter, typeSearch); }, [statusFilter, typeSearch]);

  const handleSearch = () => {
    setTypeSearch(typeInput);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString() : '—';

  const failedCount = logs.filter(l => l.status === 'failed').length;

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 min-w-0 overflow-auto">
        <div className="p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Stripe Webhook Logs</h1>
              <p className="text-slate-500 text-sm mt-1">Audit log of every incoming Stripe event — track processed, failed, and skipped webhooks</p>
            </div>
            <Button variant="outline" onClick={() => fetchLogs(page)} data-testid="refresh-webhook-logs">
              <RefreshCw size={15} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Summary */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="pt-5">
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Total (this page)</p>
                <p className="text-2xl font-bold text-slate-900">{total}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5">
                <p className="text-xs text-green-600 uppercase tracking-wide mb-1">Processed</p>
                <p className="text-2xl font-bold text-green-700">{logs.filter(l => l.status === 'processed').length}</p>
              </CardContent>
            </Card>
            <Card className={failedCount > 0 ? 'border-red-200' : ''}>
              <CardContent className="pt-5">
                <p className="text-xs text-red-500 uppercase tracking-wide mb-1">Failed</p>
                <p className={`text-2xl font-bold ${failedCount > 0 ? 'text-red-600' : 'text-slate-400'}`}>{failedCount}</p>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card className="mb-4">
            <CardContent className="pt-4 pb-4">
              <div className="flex flex-wrap items-center gap-3">
                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-40" data-testid="webhook-status-filter">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="processed">Processed</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="skipped">Skipped</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex gap-2 flex-1 max-w-sm">
                  <Input
                    placeholder="Filter by event type…"
                    value={typeInput}
                    onChange={(e) => setTypeInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    data-testid="webhook-type-search"
                  />
                  <Button variant="outline" onClick={handleSearch}><Search size={15} /></Button>
                </div>
                {(statusFilter !== 'all' || typeSearch) && (
                  <Button variant="ghost" size="sm" onClick={() => { setStatusFilter('all'); setTypeSearch(''); setTypeInput(''); }}>
                    Clear filters
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <RefreshCw size={24} className="animate-spin text-slate-400" />
                </div>
              ) : logs.length === 0 ? (
                <div className="text-center py-16 text-slate-400">
                  <Clock size={40} className="mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No webhook events yet</p>
                  <p className="text-sm">Events will appear here after the first Stripe webhook fires</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b bg-slate-50">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Event Type</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Handled As</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Event ID</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Session</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Amount</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Received</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Error</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {logs.map((log) => (
                        <tr key={log.log_id} className={`hover:bg-slate-50/80 ${log.status === 'failed' ? 'bg-red-50/40' : ''}`} data-testid={`webhook-log-${log.log_id}`}>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${STATUS_STYLES[log.status] || STATUS_STYLES.skipped}`}>
                              {STATUS_ICONS[log.status]}
                              {log.status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded text-slate-700">{log.event_type || '—'}</code>
                          </td>
                          <td className="px-4 py-3">
                            {log.handled_as ? (
                              <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${HANDLED_AS_STYLES[log.handled_as] || 'bg-slate-100 text-slate-600'}`}>
                                {log.handled_as.replace('_', ' ')}
                              </span>
                            ) : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-mono text-xs text-slate-500 truncate max-w-[140px] block" title={log.event_id}>
                              {log.event_id ? log.event_id.substring(0, 20) + '…' : '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-mono text-xs text-slate-500 truncate max-w-[120px] block" title={log.session_id}>
                              {log.session_id ? log.session_id.substring(0, 16) + '…' : '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-slate-700">
                            {log.amount != null ? `$${(log.amount / 100).toFixed(2)}` : '—'}
                          </td>
                          <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{formatDate(log.received_at)}</td>
                          <td className="px-4 py-3">
                            {log.error ? (
                              <span className="text-xs text-red-600 truncate max-w-[200px] block" title={log.error}>{log.error}</span>
                            ) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
              <span>Page {page} of {totalPages} ({total} total)</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => fetchLogs(page - 1)}>
                  <ChevronLeft size={15} />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => fetchLogs(page + 1)}>
                  <ChevronRight size={15} />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
